import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code2, TestTube, Bug, Zap } from 'lucide-react';
import { personalInfo } from '../data/mock';

const Home = () => {
  const features = [
    {
      icon: <TestTube className="w-8 h-8" />,
      title: "Automated Testing",
      description: "Comprehensive test automation frameworks"
    },
    {
      icon: <Bug className="w-8 h-8" />,
      title: "AI-Assisted QA",
      description: "Test ideation and automation with human-reviewed AI support"
    },
    {
      icon: <Code2 className="w-8 h-8" />,
      title: "Quality Assurance",
      description: "End-to-end quality assurance processes"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Performance Testing",
      description: "Load testing and performance optimization"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            {/* Animated greeting */}
            <div className="mb-8">
              <p className="text-lg text-blue-600 dark:text-blue-400 font-medium mb-4 animate-fade-in">
                Hello, I'm
              </p>
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-4 animate-slide-in">
                {personalInfo.name}
              </h1>
              <div className="text-2xl md:text-3xl text-gray-600 dark:text-gray-300 font-light animate-slide-in-delay">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {personalInfo.title}
                </span>
              </div>
            </div>

            {/* Brief description */}
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in-delay">
              I help clients prepare reliable releases through end-to-end QA ownership, API and integration testing, performance analysis, and AI-assisted automation. You receive clear test evidence, reproducible defects, and practical recommendations for your product.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <Link
                to="/projects"
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                View Projects
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Explore My Services
              </Link>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-20">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-200 dark:border-gray-700 animate-fade-in-stagger"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-blue-600 dark:text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-300 dark:bg-blue-700 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-purple-300 dark:bg-purple-700 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-300 dark:bg-pink-700 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">2+</div>
              <div className="text-gray-700 dark:text-gray-300">Years Experience</div>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">20+</div>
              <div className="text-gray-700 dark:text-gray-300">Projects Tested</div>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-pink-600 dark:text-pink-400 mb-2">99%</div>
              <div className="text-gray-700 dark:text-gray-300">Bug Detection Rate</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
